import React, { useState } from 'react'
import {
  FileText,
  Plus,
  Filter,
  Search,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Eye,
  Edit3,
  Check,
  X,
  RefreshCw,
  Clock,
  AlertCircle,
  ArrowRight,
  Info,
  Zap,
  Upload,
  Mail,
  Layers,
  Save,
  PanelRightClose,
  DollarSign,
  History,
  Bot,
  Minimize2,
} from 'lucide-react'
import DatePicker from '../../components/DatePicker'

// ─── Section Wrapper (matches DashboardGuide / TemplatesGuide style) ─────────
function Section({ number, title, children }) {
  return (
    <section className="space-y-4" data-section-number={number}>
      <div className="flex items-center gap-2">
        <span className="w-6 h-6 rounded-full bg-blue-600 text-white text-xs font-bold flex items-center justify-center">{number}</span>
        <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">{title}</h3>
      </div>
      {children}
    </section>
  )
}

// ─── Section 1: Page Header Mockup ──────────────────────────────────────────
function PageHeaderMockup() {
  return (
    <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Requests</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Manage and track all document processing requests</p>
        </div>
        <button className="inline-flex items-center gap-2 px-4 py-2.5 bg-navy-800 text-white text-sm font-medium rounded-lg hover:bg-navy-900 transition-colors cursor-default">
          <Plus className="h-4 w-4" />
          New Request
        </button>
      </div>
    </div>
  )
}

// ─── Section 2: Filters Panel Mockup ────────────────────────────────────────
function FiltersPanelMockup() {
  const [draftSearch, setDraftSearch] = useState('')

  return (
    <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden">
      <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-800">
        <div className="mb-4">
          <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">All Requests</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">47 requests found</p>
        </div>

        {/* Filters Row */}
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <label className="block text-sm text-gray-500 dark:text-gray-400 mb-1.5">Search</label>
            <input
              type="text"
              value={draftSearch}
              onChange={(e) => setDraftSearch(e.target.value)}
              placeholder="Title, description, documents..."
              className="px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500/20 focus:border-navy-500 min-w-[220px] dark:bg-gray-800 dark:text-gray-200"
            />
          </div>
          <div>
            <label className="block text-sm text-gray-500 dark:text-gray-400 mb-1.5">Date Range</label>
            <div className="flex items-center gap-2">
              <DatePicker value="" onChange={() => {}} placeholder="From" ariaLabel="From date" />
              <ArrowRight className="h-3.5 w-3.5 text-gray-400 dark:text-gray-500 flex-shrink-0" />
              <DatePicker value="" onChange={() => {}} placeholder="To" ariaLabel="To date" />
            </div>
          </div>
          <div>
            <label className="block text-sm text-gray-500 dark:text-gray-400 mb-1.5">Status</label>
            <button className="flex items-center justify-between gap-2 px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg text-sm min-w-[180px] dark:bg-gray-800 dark:text-gray-200 bg-white text-left cursor-default">
              <span className="text-gray-400">All Status</span>
              <ChevronDown className="h-3.5 w-3.5 text-gray-400" />
            </button>
          </div>
          <div>
            <label className="block text-sm text-gray-500 dark:text-gray-400 mb-1.5">Issuer</label>
            <input
              type="text"
              placeholder="Search issuer..."
              className="px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg text-sm focus:outline-none min-w-[200px] dark:bg-gray-800 dark:text-gray-200"
              readOnly
            />
          </div>
          <div>
            <label className="block text-sm text-gray-500 dark:text-gray-400 mb-1.5">Template</label>
            <button className="flex items-center justify-between gap-2 px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg text-sm min-w-[180px] dark:bg-gray-800 dark:text-gray-200 bg-white text-left cursor-default">
              <span className="text-gray-400">All Templates</span>
              <ChevronDown className="h-3.5 w-3.5 text-gray-400" />
            </button>
          </div>
          <button className="inline-flex items-center gap-2 px-6 py-2 bg-navy-800 text-white text-sm font-medium rounded-lg cursor-default">
            <Filter className="h-4 w-4" />
            Apply
          </button>
          <button className="inline-flex items-center gap-1.5 px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-500 dark:text-gray-400 cursor-default">
            <RefreshCw className="h-3.5 w-3.5" />
            Reset
          </button>
        </div>

        {/* Active filter tags example */}
        <div className="flex flex-wrap items-center gap-2 mt-3 pt-3 border-t border-gray-100 dark:border-gray-800">
          <span className="text-xs text-gray-500 dark:text-gray-400">Active filters:</span>
          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-navy-50 dark:bg-navy-900/20 text-navy-700 dark:text-navy-300 rounded text-xs">
            Status: Pending, Reviewing
          </span>
          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-navy-50 dark:bg-navy-900/20 text-navy-700 dark:text-navy-300 rounded text-xs">
            Template: Purchase Order Extraction
          </span>
        </div>
      </div>
    </div>
  )
}

// ─── Section 3: Requests Table Mockup ───────────────────────────────────────
function RequestsTableMockup() {
  const requests = [
    { id: 1, title: 'Invoice #INV-2024-0142', desc: 'Monthly subscription invoice from Acme Corp', issuer: 'accounts@acme.com', date: 'May 20, 2025', time: '14:32', status: 'reviewing', confidence: 92, createdBy: 'John Smith', docs: 2 },
    { id: 2, title: 'PO #PO-2024-0089', desc: 'Office supplies purchase order', issuer: 'procurement@vendor.io', date: 'May 19, 2025', time: '09:15', status: 'extracted', confidence: 87, createdBy: 'Sarah Wilson', docs: 1 },
    { id: 3, title: 'Contract Amendment #CA-33', desc: 'Updated terms for Q4 agreement', issuer: 'legal@partner.com', date: 'May 18, 2025', time: '16:45', status: 'pending', confidence: 0, createdBy: 'Mike Johnson', docs: 3 },
    { id: 4, title: 'Invoice #INV-2024-0141', desc: 'Cloud hosting services - April', issuer: 'billing@cloudhost.net', date: 'May 17, 2025', time: '11:20', status: 'completed', confidence: 95, createdBy: 'Emily Chen', docs: 1 },
    { id: 5, title: 'PO #PO-2024-0088', desc: 'IT equipment order', issuer: 'sales@techsupply.co', date: 'May 16, 2025', time: '08:50', status: 'failed', confidence: 23, createdBy: 'John Smith', docs: 2 },
  ]

  const getStatusConfig = (status) => {
    const config = {
      pending: { bg: 'bg-yellow-50 dark:bg-yellow-900/20', text: 'text-yellow-700 dark:text-yellow-300', dot: 'bg-yellow-500', label: 'Pending' },
      processing: { bg: 'bg-blue-50 dark:bg-blue-900/20', text: 'text-blue-600 dark:text-blue-300', dot: 'bg-blue-500', label: 'Processing' },
      extracted: { bg: 'bg-indigo-50 dark:bg-indigo-900/20', text: 'text-indigo-600 dark:text-indigo-300', dot: 'bg-indigo-500', label: 'Extracted' },
      reviewing: { bg: 'bg-orange-50 dark:bg-orange-900/20', text: 'text-orange-600 dark:text-orange-300', dot: 'bg-orange-500', label: 'Reviewing' },
      approved: { bg: 'bg-green-50 dark:bg-green-900/20', text: 'text-green-600 dark:text-green-300', dot: 'bg-green-500', label: 'Approved' },
      completed: { bg: 'bg-green-50 dark:bg-green-900/20', text: 'text-green-600 dark:text-green-300', dot: 'bg-green-500', label: 'Completed' },
      failed: { bg: 'bg-red-50 dark:bg-red-900/20', text: 'text-red-600 dark:text-red-300', dot: 'bg-red-500', label: 'Failed' },
      cancelled: { bg: 'bg-gray-50 dark:bg-gray-700', text: 'text-gray-600 dark:text-gray-300', dot: 'bg-gray-500', label: 'Cancelled' },
    }
    return config[status] || config.pending
  }

  const getIconColor = (status) => {
    const colors = {
      pending: 'text-yellow-600 bg-yellow-50 dark:text-yellow-400 dark:bg-yellow-900/20',
      extracted: 'text-indigo-600 bg-indigo-50 dark:text-indigo-400 dark:bg-indigo-900/20',
      reviewing: 'text-orange-600 bg-orange-50 dark:text-orange-400 dark:bg-orange-900/20',
      completed: 'text-green-600 bg-green-50 dark:text-green-400 dark:bg-green-900/20',
      failed: 'text-red-600 bg-red-50 dark:text-red-400 dark:bg-red-900/20',
    }
    return colors[status] || 'text-gray-600 bg-gray-50 dark:text-gray-400 dark:bg-gray-700'
  }

  return (
    <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden">
      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="bg-gray-50 dark:bg-gray-800/50 border-b border-gray-100 dark:border-gray-800">
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Request Name</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Issuer</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Created Date</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">AI Confidence</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Created By</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
            {requests.map((req) => {
              const statusConfig = getStatusConfig(req.status)
              const initials = req.createdBy.split(' ').map(n => n[0]).join('')
              return (
                <tr key={req.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center relative ${getIconColor(req.status)}`}>
                        <FileText className="h-5 w-5" />
                        <span className="absolute -top-1 -right-1 bg-navy-600 text-white text-[10px] font-medium rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">
                          {req.docs}
                        </span>
                      </div>
                      <div className="max-w-xs">
                        <p className="font-medium text-gray-900 dark:text-gray-100 text-sm">{req.title}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{req.desc}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm text-gray-600 dark:text-gray-400">{req.issuer}</span>
                  </td>
                  <td className="px-4 py-3">
                    <p className="text-sm text-gray-900 dark:text-gray-100">{req.date}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{req.time}</p>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${statusConfig.bg} ${statusConfig.text}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${statusConfig.dot}`}></span>
                      {statusConfig.label}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className="w-16 h-1.5 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full ${req.confidence >= 90 ? 'bg-green-500' : req.confidence >= 70 ? 'bg-yellow-500' : 'bg-red-500'}`}
                          style={{ width: `${req.confidence}%` }}
                        />
                      </div>
                      <span className="text-sm text-gray-600 dark:text-gray-400">{req.confidence}%</span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-xs font-medium text-gray-600 dark:text-gray-300">
                        {initials}
                      </div>
                      <span className="text-sm text-gray-600 dark:text-gray-400">{req.createdBy}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    {req.status === 'completed' || req.status === 'cancelled' ? (
                      <span className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 inline-flex">
                        <Eye className="h-4 w-4" />
                      </span>
                    ) : (
                      <span className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 inline-flex">
                        <Edit3 className="h-4 w-4" />
                      </span>
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="px-5 py-4 border-t border-gray-100 dark:border-gray-800 flex items-center justify-between">
        <div className="flex items-center gap-4 text-sm text-gray-500 dark:text-gray-400">
          <span>Showing 1–10 of 47 results</span>
          <span className="border-l border-gray-200 dark:border-gray-700 h-4" />
          <span className="flex items-center gap-1.5">
            Rows per page
            <span className="px-2 py-1 border border-gray-200 dark:border-gray-700 rounded text-sm dark:bg-gray-800 dark:text-gray-200">10</span>
          </span>
        </div>
        <div className="flex items-center gap-1">
          <button className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-500 dark:text-gray-400 rounded opacity-50 cursor-default">
            <ChevronLeft className="h-4 w-4" />
            Previous
          </button>
          <button className="w-8 h-8 rounded text-sm font-medium bg-navy-800 text-white">1</button>
          <button className="w-8 h-8 rounded text-sm font-medium text-gray-600 dark:text-gray-400">2</button>
          <button className="w-8 h-8 rounded text-sm font-medium text-gray-600 dark:text-gray-400">3</button>
          <span className="px-2 text-gray-400 dark:text-gray-500">...</span>
          <button className="w-8 h-8 rounded text-sm font-medium text-gray-600 dark:text-gray-400">5</button>
          <button className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-500 dark:text-gray-400 rounded cursor-default">
            Next
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Section 4: Status Lifecycle Mockup ─────────────────────────────────────
function StatusLifecycleMockup() {
  const statuses = [
    { label: 'Pending', dot: 'bg-yellow-500', desc: 'Waiting to be processed' },
    { label: 'Processing', dot: 'bg-blue-500', desc: 'AI is extracting data' },
    { label: 'Extracted', dot: 'bg-indigo-500', desc: 'Extraction complete, ready for review' },
    { label: 'Reviewing', dot: 'bg-orange-500', desc: 'Human reviewing extracted fields' },
    { label: 'Approved', dot: 'bg-green-500', desc: 'Fields approved, ready to publish' },
    { label: 'Completed', dot: 'bg-green-500', desc: 'Finalized and archived' },
  ]

  const altStatuses = [
    { label: 'Failed', dot: 'bg-red-500', desc: 'Extraction encountered an error' },
    { label: 'Rejected', dot: 'bg-red-500', desc: 'Request was rejected by reviewer' },
    { label: 'Cancelled', dot: 'bg-gray-500', desc: 'Manually cancelled by user' },
  ]

  return (
    <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-5">
      <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">Each request follows a lifecycle through these statuses:</p>
      
      {/* Main flow */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        {statuses.map((s, i) => (
          <React.Fragment key={s.label}>
            <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <span className={`w-2 h-2 rounded-full ${s.dot}`}></span>
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{s.label}</span>
            </div>
            {i < statuses.length - 1 && (
              <ArrowRight className="h-4 w-4 text-gray-300 dark:text-gray-600 flex-shrink-0" />
            )}
          </React.Fragment>
        ))}
      </div>

      {/* Alt statuses */}
      <div className="border-t border-gray-100 dark:border-gray-800 pt-4 mt-4">
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">Alternative end states:</p>
        <div className="flex flex-wrap gap-2">
          {altStatuses.map((s) => (
            <div key={s.label} className="flex items-center gap-2 px-3 py-2 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <span className={`w-2 h-2 rounded-full ${s.dot}`}></span>
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{s.label}</span>
              <span className="text-xs text-gray-400 dark:text-gray-500">- {s.desc}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

// ─── Section 5: New Request Modal Mockup ────────────────────────────────────
function NewRequestMockup() {
  return (
    <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden max-w-xl">
      {/* Modal Header */}
      <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-navy-50 dark:bg-navy-900/20 rounded-lg">
            <Mail className="h-5 w-5 text-navy-600 dark:text-navy-400" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900 dark:text-gray-100">Ingest Email</h3>
            <p className="text-xs text-gray-500 dark:text-gray-400">Create a new request from an email</p>
          </div>
        </div>
        <button className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 cursor-default">
          <X className="h-5 w-5" />
        </button>
      </div>

      {/* Modal Body */}
      <div className="px-5 py-4 space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Subject</label>
          <input
            type="text"
            value="Invoice #INV-2024-0142 from Acme Corp"
            readOnly
            className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg text-sm dark:bg-gray-800 dark:text-gray-200"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Sender</label>
          <input
            type="text"
            value="accounts@acme.com"
            readOnly
            className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg text-sm dark:bg-gray-800 dark:text-gray-200"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Template</label>
          <div className="flex items-center justify-between px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg text-sm dark:bg-gray-800 dark:text-gray-200">
            <span>Purchase Order Extraction</span>
            <ChevronDown className="h-3.5 w-3.5 text-gray-400" />
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Attachments</label>
          <div className="border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-lg p-4 text-center">
            <Upload className="h-6 w-6 text-gray-400 mx-auto mb-2" />
            <p className="text-sm text-gray-500 dark:text-gray-400">Drag & drop files or click to browse</p>
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">PDF, Word, Excel, Images (max 25MB)</p>
          </div>
          <div className="mt-2 flex items-center gap-2 px-3 py-2 bg-gray-50 dark:bg-gray-800 rounded-lg">
            <FileText className="h-4 w-4 text-navy-600 dark:text-navy-400" />
            <span className="text-sm text-gray-700 dark:text-gray-300 flex-1">invoice_acme_042.pdf</span>
            <span className="text-xs text-gray-400">1.2 MB</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded border bg-navy-800 border-navy-800 flex items-center justify-center">
            <Check className="h-3 w-3 text-white" />
          </div>
          <span className="text-sm text-gray-700 dark:text-gray-300">Auto-analyze after upload</span>
        </div>
      </div>

      {/* Modal Footer */}
      <div className="px-5 py-4 border-t border-gray-100 dark:border-gray-800 flex items-center justify-end gap-3">
        <button className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-gray-700 rounded-lg cursor-default">
          Cancel
        </button>
        <button className="px-4 py-2 bg-navy-800 text-white text-sm font-medium rounded-lg cursor-default">
          Submit Request
        </button>
      </div>
    </div>
  )
}

// ─── Section 6: Request Detail View Mockup ──────────────────────────────────
function RequestDetailMockup() {
  const fields = [
    { label: 'Additional Notes', value: 'IF ANY ITEMS ARE NON-CANCELABLE / NON-RETURNABLE', confidence: 0.95 },
    { label: 'Customer Account No', value: '140439', confidence: 0.92 },
    { label: 'Customer Address City', value: 'kokapet', confidence: 0.78 },
    { label: 'Customer Address Country', value: 'United Kingdom', confidence: 0.96 },
    { label: 'Delivery Date', value: '2025-06-01', confidence: 0.65 },
  ]

  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-100 dark:bg-gray-950 overflow-hidden">
      {/* Top Action Bar */}
      <div className="bg-white dark:bg-gray-800 px-4 py-2 flex items-center justify-between border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-2">
          <button className="px-3 py-1.5 text-white text-xs font-medium rounded-lg flex items-center gap-1.5 bg-blue-500 cursor-default">
            <Edit3 className="h-3.5 w-3.5" />
            Edit
          </button>
          <button className="px-3 py-1.5 text-white text-xs font-medium rounded-lg flex items-center gap-1.5 bg-green-500 cursor-default">
            <Save className="h-3.5 w-3.5" />
            Save
          </button>
        </div>
        <div className="flex items-center gap-5 text-xs text-gray-500 dark:text-gray-400">
          <div className="flex items-center gap-1.5">
            <Clock className="h-3.5 w-3.5" />
            <span>Last saved: 52 minutes ago</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span>Template used:</span>
            <span className="font-semibold text-gray-700 dark:text-gray-200">Purchase Order Analyzer</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span>AI Confidence:</span>
            <span className="font-semibold text-green-600 dark:text-green-400">95%</span>
          </div>
          <button className="flex items-center gap-1.5 px-2 py-1 rounded-lg text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-950/40 cursor-default">
            <Info className="h-3.5 w-3.5" />
            <span className="font-medium">AI Operations</span>
          </button>
        </div>
      </div>

      {/* Document Tabs */}
      <div className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 px-4 py-2 flex items-center gap-2">
        <button className="px-3 py-1.5 text-xs font-medium rounded-lg flex items-center gap-1.5 bg-navy-800 text-white">
          <FileText className="h-3.5 w-3.5" />
          Purchase Order_PO813...
        </button>
        <button className="px-2 py-1.5 text-xs font-medium rounded-lg flex items-center gap-1 border border-dashed border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400 cursor-default">
          <Upload className="h-3.5 w-3.5" />
          Upload
        </button>
        <button className="ml-2 px-2.5 py-1.5 text-xs font-medium rounded-lg flex items-center gap-1.5 border border-gray-200 dark:border-gray-600 bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 cursor-default">
          <PanelRightClose className="h-3.5 w-3.5" />
          Hide Viewer
        </button>
        {/* Confidence legend */}
        <div className="ml-auto flex items-center gap-3 text-[10px]">
          <div className="flex items-center gap-1">
            <div className="w-2 h-2 rounded-full bg-green-500"></div>
            <span className="text-gray-500 dark:text-gray-400">&gt;90%</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-2 h-2 rounded-full bg-orange-500"></div>
            <span className="text-gray-500 dark:text-gray-400">70-89%</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-2 h-2 rounded-full bg-red-500"></div>
            <span className="text-gray-500 dark:text-gray-400">&lt;70%</span>
          </div>
        </div>
      </div>

      {/* Split Panel: Fields Left, PDF Right */}
      <div className="flex" style={{ height: '380px' }}>
        {/* Left Panel - Extracted Fields */}
        <div className="w-[380px] flex-shrink-0 bg-white dark:bg-gray-900 border-r border-gray-200 dark:border-gray-700 flex flex-col overflow-hidden">
          {/* Request Info (collapsed) */}
          <button className="w-full px-4 py-2 flex items-center justify-between text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider border-b border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 cursor-default">
            <span>Request Info</span>
            <ChevronRight className="h-3 w-3" />
          </button>

          {/* Fields Header */}
          <div className="px-4 py-2 border-b border-gray-100 dark:border-gray-700">
            <h3 className="text-xs font-semibold text-gray-900 dark:text-gray-100 mb-1.5">Extracted Data Fields</h3>
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3 w-3 text-gray-400" />
              <input
                type="text"
                placeholder="Filter fields..."
                readOnly
                className="w-full pl-7 pr-3 py-1 text-[10px] rounded-md border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300"
              />
            </div>
          </div>

          {/* Category Header */}
          <div className="px-4 pt-3 pb-1">
            <span className="text-[10px] font-semibold text-gray-900 dark:text-gray-100">Other Fields</span>
            <span className="text-[10px] text-gray-400 ml-1">(46)</span>
          </div>

          {/* Scrollable Fields - matching actual FieldInput component */}
          <div className="flex-1 overflow-y-auto px-4 pb-3">
            <div className="space-y-1">
              {fields.map((field, i) => {
                const isHigh = field.confidence >= 0.9
                const isMedium = field.confidence >= 0.7 && field.confidence < 0.9
                const confidencePercent = Math.round(field.confidence * 100)
                return (
                  <div key={i} className="py-2">
                    {/* Label row with confidence indicator */}
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-1.5">
                        <div className={`w-1.5 h-1.5 rounded-full ${isHigh ? 'bg-green-500' : isMedium ? 'bg-orange-500' : 'bg-red-500'}`}></div>
                        <span className="text-xs font-medium text-gray-600 dark:text-gray-400">{field.label}</span>
                      </div>
                      {/* Confidence badge */}
                      <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                        isHigh ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                          : isMedium ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400'
                          : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                      }`}>{confidencePercent}%</span>
                    </div>
                    {/* Value input */}
                    <input
                      type="text"
                      value={field.value}
                      readOnly
                      className="w-full px-4 py-3 text-base font-medium rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-200"
                    />
                  </div>
                )
              })}
            </div>
          </div>
        </div>

        {/* Right Panel - PDF Viewer placeholder */}
        <div className="flex-1 bg-gray-200 dark:bg-gray-800 flex flex-col items-center justify-center relative">
          {/* Page thumbnails sidebar */}
          <div className="absolute left-0 top-0 bottom-0 w-12 bg-gray-100 dark:bg-gray-900 border-r border-gray-300 dark:border-gray-700 flex flex-col items-center pt-2 gap-1.5">
            <div className="w-8 h-10 bg-white dark:bg-gray-700 rounded border-2 border-navy-500 shadow-sm"></div>
          </div>
          {/* PDF page content */}
          <div className="ml-12 flex-1 flex items-center justify-center">
            <div className="text-center">
              <div className="w-48 h-64 bg-white dark:bg-gray-700 rounded-lg shadow-lg mx-auto mb-3 flex items-center justify-center border border-gray-300 dark:border-gray-600 p-4">
                <div className="space-y-2 w-full">
                  <div className="h-5 bg-blue-600 rounded w-2/3 ml-auto"></div>
                  <div className="h-8 flex items-center">
                    <span className="text-sm font-bold text-gray-700 dark:text-gray-300">Purchase Order</span>
                  </div>
                  <div className="h-1.5 bg-gray-300 dark:bg-gray-500 rounded w-full"></div>
                  <div className="h-1.5 bg-gray-300 dark:bg-gray-500 rounded w-4/5"></div>
                  <div className="h-1.5 bg-gray-300 dark:bg-gray-500 rounded w-full"></div>
                  <div className="h-1.5 bg-gray-300 dark:bg-gray-500 rounded w-3/5"></div>
                  <div className="h-1.5 bg-gray-300 dark:bg-gray-500 rounded w-full"></div>
                  <div className="h-1.5 bg-gray-300 dark:bg-gray-500 rounded w-2/3"></div>
                </div>
              </div>
              <div className="flex items-center justify-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                <ChevronLeft className="h-3 w-3" />
                <span>Page 1 of 1</span>
                <ChevronRight className="h-3 w-3" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

// ─── Section 7: AI Confidence Mockup ────────────────────────────────────────
function ConfidenceMockup() {
  const levels = [
    { range: '90–100%', color: 'bg-green-500', label: 'High Confidence', desc: 'Data extracted accurately with minimal uncertainty' },
    { range: '70–89%', color: 'bg-yellow-500', label: 'Medium Confidence', desc: 'Likely correct but may need human verification' },
    { range: '0–69%', color: 'bg-red-500', label: 'Low Confidence', desc: 'Requires manual review and correction' },
  ]

  return (
    <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-5 max-w-2xl">
      <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
        AI Confidence shows how certain the AI is about the extracted data. Use this to prioritize which requests need manual review.
      </p>
      <div className="space-y-3">
        {levels.map((level) => (
          <div key={level.range} className="flex items-center gap-4 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
            <div className="flex items-center gap-2 min-w-[100px]">
              <div className={`w-3 h-3 rounded-full ${level.color}`}></div>
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{level.range}</span>
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{level.label}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">{level.desc}</p>
            </div>
            <div className="w-16 h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
              <div className={`h-full rounded-full ${level.color}`} style={{ width: level.range.startsWith('90') ? '95%' : level.range.startsWith('70') ? '80%' : '40%' }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

// ─── Main Guide Component ───────────────────────────────────────────────────
export default function RequestsGuide({ onNavigate }) {
  return (
    <div className="space-y-10">
      {/* Module Header */}
      <div className="flex items-center gap-4">
        <div className="w-12 h-12 rounded-xl bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
          <FileText className="h-6 w-6 text-blue-600 dark:text-blue-400" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">Requests</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">Manage and track document processing requests through the full extraction pipeline.</p>
        </div>
      </div>

      {/* Section 1: Page Header */}
      <Section number="1" title="Page Header & New Request">
        <p className="text-sm text-gray-600 dark:text-gray-400">
          The page header shows the title and a <strong>"New Request"</strong> button. Clicking it opens the Ingest Email modal to submit documents for AI extraction.
        </p>
        <PageHeaderMockup />
      </Section>

      {/* Section 2: Filters */}
      <Section number="2" title="Filtering & Search">
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Use the filter panel to narrow down requests by <strong>Search</strong> (title, description, documents), 
          <strong> Date Range</strong>, <strong>Status</strong> (multi-select), <strong>Issuer</strong>, and <strong>Template</strong>.
          Click <strong>Apply</strong> to commit filters. Active filters appear as tags. Use <strong>Reset</strong> to clear.
        </p>
        <FiltersPanelMockup />
      </Section>

      {/* Section 3: Requests Table */}
      <Section number="3" title="Requests Table">
        <p className="text-sm text-gray-600 dark:text-gray-400">
          The table shows all requests with columns for Request Name (with doc count badge), Issuer, Created Date, 
          Status (color-coded badge), AI Confidence (progress bar), Created By (avatar), and Actions (view/edit icons). Click any row to open the detail view.
        </p>
        <RequestsTableMockup />
      </Section>

      {/* Section 4: Status Lifecycle */}
      <Section number="4" title="Request Status Lifecycle">
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Each request moves through a defined lifecycle. Understanding the status flow helps you track progress in the pipeline.
        </p>
        <StatusLifecycleMockup />
      </Section>

      {/* Section 5: New Request Modal */}
      <Section number="5" title="Creating a New Request">
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Click <strong>"New Request"</strong> to open the Ingest Email modal. Enter email details (Subject, Sender), 
          select a Template, attach documents (drag & drop or browse), and toggle <strong>Auto-analyze</strong> to begin AI extraction immediately.
        </p>
        <NewRequestMockup />
      </Section>

      {/* Section 6: Request Detail */}
      <Section number="6" title="Request Detail - Split Panel View">
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Clicking a request opens a full-screen split-panel view: extracted fields on the left, PDF document viewer on the right. 
          The top action bar has <strong>Publish</strong>, <strong>Cancel</strong>, and <strong>Save</strong> buttons plus metadata (template used, AI confidence, last saved).
          Document tabs let you switch between attachments.
        </p>
        <RequestDetailMockup />
      </Section>

      {/* Section 7: AI Confidence */}
      <Section number="7" title="Understanding AI Confidence">
        <p className="text-sm text-gray-600 dark:text-gray-400">
          The AI Confidence score indicates how certain the extraction model is about each value. Use this to prioritize review effort.
        </p>
        <ConfidenceMockup />
      </Section>

      {/* Section 8: Processing Modal */}
      <Section number="8" title="Processing Modal">
        <p className="text-sm text-gray-600 dark:text-gray-400">
          When AI extraction begins, a full-screen overlay shows real-time progress. It can be minimized to a floating pill in the bottom-right corner.
        </p>

        {/* Full modal mockup */}
        <div className="relative bg-white dark:bg-gray-900 rounded-2xl shadow-xl shadow-navy-900/20 border border-navy-100 dark:border-navy-800/60 p-8 max-w-lg overflow-hidden">
            {/* Brand glow */}
            <div className="pointer-events-none absolute -top-24 left-1/2 -translate-x-1/2 w-72 h-72 rounded-full bg-navy-500/10 blur-3xl" />
            <div className="pointer-events-none absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-transparent via-navy-500/60 to-transparent" />

            <div className="relative flex flex-col items-center text-center">
              {/* Bot icon with brand ring */}
              <div className="relative mb-4 w-20 h-20 flex items-center justify-center">
                <div className="absolute inset-1 rounded-full bg-navy-500/15 blur-md" />
                <svg className="absolute inset-0 w-20 h-20 text-navy-500" viewBox="0 0 100 100" fill="none">
                  <circle cx="50" cy="50" r="46" stroke="currentColor" strokeOpacity="0.15" strokeWidth="3" />
                  <circle cx="50" cy="50" r="46" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeDasharray="70 220" />
                </svg>
                <div className="relative w-16 h-16 bg-gradient-to-br from-navy-500 to-navy-700 rounded-full flex items-center justify-center shadow-lg shadow-navy-900/25 ring-1 ring-white/10">
                  <Bot className="h-8 w-8 text-white" />
                </div>
              </div>

              <p className="text-lg font-bold text-gray-900 dark:text-gray-100 tracking-tight">AI is Extracting Details</p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 mb-5">Hang tight — this usually only takes a moment.</p>

              {/* Progress bar */}
              <div className="w-full mb-3">
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-xs font-medium text-gray-600 dark:text-gray-300">Analysing document structure...</span>
                  <span className="text-xs font-bold text-navy-600 dark:text-navy-400 tabular-nums">65%</span>
                </div>
                <div className="relative w-full h-2 bg-gray-200 dark:bg-gray-800 rounded-full overflow-hidden">
                  <div className="absolute inset-y-0 left-0 bg-gradient-to-r from-navy-500 to-navy-600 rounded-full" style={{ width: '65%' }}>
                    <div className="absolute inset-0 progress-shimmer rounded-full" />
                  </div>
                </div>
              </div>

              {/* Real-time log */}
              <div className="w-full bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 max-h-36 overflow-y-auto text-left divide-y divide-gray-100 dark:divide-gray-700">
                {[
                  { time: '0.2s', msg: '✅ Starting extraction...', color: 'text-green-600 dark:text-green-400 font-medium' },
                  { time: '1.4s', msg: '✅ Document uploaded to Azure CU', color: 'text-green-600 dark:text-green-400 font-medium' },
                  { time: '2.8s', msg: '✅ Email body analysed via OpenAI', color: 'text-green-600 dark:text-green-400 font-medium' },
                  { time: '3.1s', msg: '⏳ Analysing document structure...', color: 'text-amber-600 dark:text-amber-400' },
                ].map((log, i) => (
                  <div key={i} className="px-3 py-1.5 flex items-start gap-2">
                    <span className="text-[10px] font-mono text-gray-400 dark:text-gray-500 whitespace-nowrap mt-0.5">{log.time}</span>
                    <span className={`text-[10px] ${log.color}`}>{log.msg}</span>
                  </div>
                ))}
              </div>

              {/* Footer */}
              <div className="mt-3 pt-2 border-t border-gray-100 dark:border-gray-700 w-full flex items-center justify-between">
                <span className="text-[10px] text-gray-400">Request ID: REQ-0042</span>
                <span className="text-[10px] text-gray-400">Elapsed: 3s</span>
              </div>
              <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-2">Click anywhere outside to minimize</p>
            </div>
        </div>

        {/* Minimized pill mockup */}
        <div className="mt-4">
          <p className="text-[10px] font-semibold text-gray-500 uppercase mb-2">Minimized State (bottom-right pill)</p>
          <div className="inline-flex items-center gap-3 pl-3 pr-4 py-2.5 bg-white/95 dark:bg-gray-900/95 backdrop-blur border border-navy-200/70 dark:border-navy-800/70 rounded-full shadow-lg shadow-navy-900/10">
            <div className="relative w-8 h-8 flex items-center justify-center">
              <svg className="absolute inset-0 w-8 h-8 text-navy-500" viewBox="0 0 100 100" fill="none">
                <circle cx="50" cy="50" r="46" stroke="currentColor" strokeOpacity="0.15" strokeWidth="6" />
                <circle cx="50" cy="50" r="46" stroke="currentColor" strokeWidth="6" strokeLinecap="round" strokeDasharray="70 220" />
              </svg>
              <div className="relative w-6 h-6 bg-gradient-to-br from-navy-500 to-navy-700 rounded-full flex items-center justify-center ring-1 ring-white/10">
                <Bot className="h-3.5 w-3.5 text-white" />
              </div>
            </div>
            <div>
              <p className="text-xs font-semibold text-gray-900 dark:text-gray-100">Extracting…</p>
              <p className="text-[10px] text-navy-600 dark:text-navy-400 font-medium tabular-nums">65% · click to expand</p>
            </div>
            <Minimize2 className="h-3.5 w-3.5 text-gray-400 ml-1" />
          </div>
        </div>

        <div className="mt-3 space-y-2 text-sm text-gray-600 dark:text-gray-400">
          <p><strong>Full modal:</strong> Shows spinning Bot icon, progress bar with percentage, and a real-time scrollable log with color-coded messages.</p>
          <p><strong>Minimized pill:</strong> Fixed to bottom-right of the viewport. Shows gradient Bot icon, percentage, and "click to expand" hint.</p>
          <p><strong>Log colors:</strong> ✅ green = success, ❌ red = error, ⏳ amber = waiting/polling, default = gray.</p>
        </div>
      </Section>

      {/* Section 9: Edit Mode & Audit Trail */}
      <Section number="9" title="Edit Mode & Audit Trail">
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Published or approved requests can be re-edited. Changes require a reason and are tracked in the audit log.
        </p>

        {/* Action bar mockup */}
        <div className="rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden max-w-lg">
          {/* Top action bar - view mode */}
          <div className="bg-white dark:bg-gray-800 px-4 py-2 flex items-center justify-between border-b border-gray-200 dark:border-gray-700">
            <p className="text-[10px] font-semibold text-gray-400 uppercase">View Mode (Published Request)</p>
            <div className="flex items-center gap-2">
              <button className="px-4 py-1.5 text-white text-sm font-medium rounded-lg flex items-center gap-1.5 bg-blue-500">
                <Edit3 className="h-3.5 w-3.5" /> Edit
              </button>
              <button className="px-4 py-1.5 text-white text-sm font-medium rounded-lg flex items-center gap-1.5 bg-gray-400 cursor-not-allowed opacity-50">
                <Save className="h-3.5 w-3.5" /> Save
              </button>
            </div>
          </div>

          {/* Top action bar - edit mode */}
          <div className="bg-white dark:bg-gray-800 px-4 py-2 flex items-center justify-between border-b border-gray-200 dark:border-gray-700">
            <p className="text-[10px] font-semibold text-gray-400 uppercase">Edit Mode (Changes Made)</p>
            <div className="flex items-center gap-2">
              <button className="px-4 py-1.5 text-white text-sm font-medium rounded-lg flex items-center gap-1.5 bg-red-500">
                <X className="h-3.5 w-3.5" /> Discard Changes
              </button>
              <button className="px-4 py-1.5 text-white text-sm font-medium rounded-lg flex items-center gap-1.5 bg-blue-500">
                <Save className="h-3.5 w-3.5" /> Save
              </button>
            </div>
          </div>
        </div>

        {/* Save Reason Modal */}
        <div className="mt-4">
          <p className="text-[10px] font-semibold text-gray-500 uppercase mb-2">Save Reason Modal</p>
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 p-6 max-w-md">
              <p className="text-base font-semibold text-gray-900 dark:text-gray-100">Save Changes</p>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Please provide a reason for the changes you are making. This will be recorded in the audit log.</p>
              <textarea
                readOnly
                rows={3}
                placeholder="Enter reason for changes..."
                className="w-full mt-3 px-4 py-3 text-sm border border-gray-200 dark:border-gray-600 rounded-lg resize-none dark:bg-gray-800 dark:text-gray-200 dark:placeholder-gray-500"
              />
              <div className="flex justify-end gap-2 mt-4">
                <button className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-700 text-sm text-gray-700 dark:text-gray-200">Cancel</button>
                <button className="px-4 py-2 rounded-lg bg-blue-500 text-white text-sm font-medium flex items-center gap-1.5">
                  <Check className="h-3.5 w-3.5" /> OK
                </button>
              </div>
          </div>
        </div>

        {/* Audit trail per-field */}
        <div className="mt-4">
          <p className="text-[10px] font-semibold text-gray-500 uppercase mb-2">Per-Field Audit Trail (History Icon)</p>
          <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-4 max-w-sm">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs text-gray-700 dark:text-gray-300">Issuer Name</span>
              <button className="p-1.5 rounded-md bg-purple-600 text-white">
                <History className="h-3.5 w-3.5" />
              </button>
            </div>
            {/* History popup */}
            <div className="bg-white dark:bg-gray-900 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
              <div className="bg-purple-50 dark:bg-purple-900/30 border-b border-purple-100 dark:border-purple-800 px-3 py-2">
                <p className="text-xs font-semibold text-purple-900 dark:text-purple-200">Edit History</p>
              </div>
              <div className="p-3 space-y-2">
                <div>
                  <p className="text-[10px] text-gray-600 dark:text-gray-400">John Doe • 2 hours ago</p>
                  <div className="flex items-center gap-1.5 mt-1">
                    <span className="text-[9px] px-1.5 py-0.5 rounded bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400">ABC Co</span>
                    <ArrowRight className="h-2.5 w-2.5 text-gray-400" />
                    <span className="text-[9px] px-1.5 py-0.5 rounded bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400">ABC Corporation</span>
                  </div>
                  <p className="text-[9px] text-gray-400 italic mt-1">"Corrected full legal name per client request"</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-3 space-y-2 text-sm text-gray-600 dark:text-gray-400">
          <p><strong>Workflow:</strong> Click "Edit" → modify fields → click "Save" → enter reason in modal → changes recorded with before/after values.</p>
          <p><strong>Discard:</strong> "Discard Changes" (red) reverts all edits and exits edit mode.</p>
          <p><strong>History icon:</strong> Each field has a clock icon. Purple = has edit history. Click to see all prior changes with from/to chips and reasons.</p>
        </div>
      </Section>

      {/* Section 10: Cancel Request */}
      <Section number="10" title="Cancelling a Request">
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Requests in Reviewing, Extracted, or Pending status can be cancelled. A reason is required for audit purposes.
        </p>

        {/* Cancel button in action bar */}
        <div className="mb-3">
          <p className="text-[10px] font-semibold text-gray-500 uppercase mb-2">Cancel Button (Action Bar)</p>
          <button className="px-4 py-1.5 text-white text-sm font-medium rounded-lg flex items-center gap-1.5 bg-red-500">
            <X className="h-3.5 w-3.5" /> Cancel
          </button>
        </div>

        {/* Cancel reason modal */}
        <div className="bg-white dark:bg-gray-900 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 p-6 max-w-md">
            <p className="text-base font-semibold text-gray-900 dark:text-gray-100">Cancel Request</p>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Please provide a reason for cancelling this request. This will be recorded in the audit log.</p>

            {/* Error state example */}
            <div className="mt-3 p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg flex items-center gap-2">
              <AlertCircle className="h-4 w-4 text-red-500 flex-shrink-0" />
              <span className="text-xs text-red-700 dark:text-red-300">Reason is required</span>
            </div>

            <textarea
              readOnly
              rows={3}
              placeholder="Enter reason for cancellation..."
              className="w-full mt-3 px-4 py-3 text-sm border border-gray-200 dark:border-gray-600 rounded-lg resize-none focus:ring-2 focus:ring-red-500 dark:bg-gray-800 dark:text-gray-200 dark:placeholder-gray-500"
            />
            <div className="flex justify-end gap-2 mt-4">
              <button className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-700 text-sm text-gray-700 dark:text-gray-200">Go Back</button>
              <button className="px-4 py-2 rounded-lg bg-red-500 text-white text-sm font-medium flex items-center gap-1.5">
                <X className="h-3.5 w-3.5" /> Confirm Cancel
              </button>
            </div>
        </div>

        <div className="mt-3 space-y-2 text-sm text-gray-600 dark:text-gray-400">
          <p><strong>Cancel button:</strong> Red button with X icon appears in the action bar for non-published requests.</p>
          <p><strong>Modal:</strong> Requires a non-empty reason. Focus ring is red. Validation error shown in red alert box if submitted empty.</p>
          <p><strong>Result:</strong> Request status changes to "Cancelled". Cannot be reactivated. Displayed as read-only with italic "Request Cancelled - Read Only" label.</p>
        </div>
      </Section>

      {/* Section 11: Cost Breakdown */}
      <Section number="11" title="AI Operations & Cost Breakdown">
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Click <strong>"AI Operations"</strong> in the action bar to view a detailed breakdown of all AI processing costs for the request.
        </p>

        {/* Trigger button */}
        <div className="mb-3">
          <p className="text-[10px] font-semibold text-gray-500 uppercase mb-2">Trigger Button (Action Bar)</p>
          <button className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-blue-50 dark:bg-blue-950/40 text-blue-600 dark:text-blue-400">
            <Info className="h-4 w-4" />
            <span className="text-xs font-medium">AI Operations</span>
          </button>
        </div>

        {/* Cost modal mockup */}
        <div className="bg-white dark:bg-gray-900 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 max-w-md overflow-hidden">
            {/* Header with gradient */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-950/40 dark:to-indigo-950/40 px-5 py-3 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
                  <Zap className="h-4 w-4 text-white" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">AI Processing Summary</p>
                  <p className="text-[10px] text-gray-500">All Azure AI operations & costs</p>
                </div>
              </div>
              <button className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700">
                <X className="h-4 w-4 text-gray-500" />
              </button>
            </div>

            {/* Summary cards */}
            <div className="px-5 pt-4">
              <div className="grid grid-cols-4 gap-2.5 mb-4">
                {[
                  { label: 'DOCUMENTS', value: '1' },
                  { label: 'PAGES', value: '1' },
                  { label: 'TOKENS', value: '19.9K' },
                  { label: 'TIME', value: '59.0s' },
                ].map((stat) => (
                  <div key={stat.label} className="bg-gray-50 dark:bg-gray-800 rounded-lg p-2.5 text-center border border-gray-200 dark:border-gray-700">
                    <p className="text-[9px] text-gray-500 uppercase tracking-wider">{stat.label}</p>
                    <p className="text-lg font-bold text-gray-900 dark:text-white">{stat.value}</p>
                  </div>
                ))}
              </div>

              {/* Operations list */}
              <p className="text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider mb-2">AI Operations Performed</p>
              <div className="space-y-2 mb-4">
                {[
                  { name: 'Azure Content Understanding', cost: '$0.0364', barPct: 85, borderColor: 'border-l-blue-500', barColor: 'bg-blue-500' },
                  { name: 'LLM Fallback Extraction', cost: '$0.0311', barPct: 70, borderColor: 'border-l-orange-400', barColor: 'bg-orange-400' },
                  { name: 'AI Field Normalisation', cost: '$0.0430', barPct: 100, borderColor: 'border-l-green-500', barColor: 'bg-green-500' },
                ].map((op) => (
                  <div key={op.name} className={`rounded-lg border border-gray-200 dark:border-gray-700 border-l-4 ${op.borderColor} overflow-hidden`}>
                    <div className="flex items-center gap-3 px-3 py-2.5">
                      <span className="text-xs font-medium text-gray-900 dark:text-gray-100 flex-1">{op.name}</span>
                      <span className="text-xs font-bold text-gray-900 dark:text-gray-100">{op.cost}</span>
                      <ChevronRight className="h-3.5 w-3.5 text-gray-400" />
                    </div>
                    <div className="h-1 bg-gray-100 dark:bg-gray-700 mx-3 mb-2 rounded-full">
                      <div className={`h-full ${op.barColor} rounded-full`} style={{ width: `${op.barPct}%` }} />
                    </div>
                  </div>
                ))}
              </div>

              {/* Per-document breakdown */}
              <p className="text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider mb-2">Per-Document Breakdown</p>
              <div className="bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 mb-4">
                <div className="flex items-center gap-3 px-3 py-2.5">
                  <FileText className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-gray-900 dark:text-gray-100 truncate">4501749135 (1).pdf</p>
                    <p className="text-[10px] text-gray-500">1 std pages → Azure CU • 14.98s</p>
                  </div>
                  <span className="text-xs font-medium text-gray-700 dark:text-gray-300">$0.0364</span>
                </div>
              </div>

              {/* Token Usage Summary */}
              <div className="bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-3 mb-4">
                <p className="text-xs font-semibold text-gray-900 dark:text-gray-100 mb-2">Token Usage Summary</p>
                <div className="grid grid-cols-3 gap-3 text-center">
                  <div>
                    <p className="text-[10px] text-gray-500">Input</p>
                    <p className="text-base font-bold text-gray-900 dark:text-white">14.6K</p>
                    <p className="text-[10px] text-gray-400">$0.0255</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-gray-500">Output</p>
                    <p className="text-base font-bold text-gray-900 dark:text-white">5.4K</p>
                    <p className="text-[10px] text-gray-400">$0.0750</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-gray-500">Total</p>
                    <p className="text-base font-bold text-gray-900 dark:text-white">19.9K</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Total cost footer */}
            <div className="px-5 py-3 border-t border-gray-200 dark:border-gray-700 bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-950/30 dark:to-emerald-950/30 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <DollarSign className="h-4 w-4 text-green-600" />
                <span className="text-sm font-semibold text-green-900 dark:text-green-200">Total AI Cost</span>
              </div>
              <div className="text-right">
                <p className="text-lg font-bold text-green-700 dark:text-green-300">$0.1105</p>
                <p className="text-[10px] text-green-600 dark:text-green-400">Avg: $0.1105/page • $0.1105/document</p>
              </div>
            </div>
        </div>

        <div className="mt-3 space-y-2 text-sm text-gray-600 dark:text-gray-400">
          <p><strong>Summary cards:</strong> Documents processed, total pages, token count, and elapsed time at a glance.</p>
          <p><strong>Operations:</strong> Each expandable to show token counts, input/output costs, and page fees. Cost bars show relative proportion.</p>
          <p><strong>Per-document:</strong> Shows which AI service processed each file (Azure CU for documents, OpenAI for email bodies).</p>
          <p><strong>Total cost:</strong> Green footer with average cost per page and per document.</p>
        </div>
      </Section>

      {/* Section 12: Activity & Processing Log */}
      <Section number="12" title="Activity Log & Processing Log">
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Two collapsible log sections appear below the extracted fields in the detail view.
        </p>

        {/* Processing Log mockup */}
        <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 overflow-hidden max-w-lg">
          {/* Processing Log header */}
          <div className="px-4 py-2.5 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Clock className="w-3.5 h-3.5 text-blue-500" />
              <span className="text-xs font-semibold text-gray-900 dark:text-gray-100">Processing Log</span>
              <span className="text-[10px] text-gray-400">(6 steps)</span>
            </div>
            <ChevronDown className="h-3.5 w-3.5 text-gray-400" />
          </div>

          {/* Terminal-style log */}
          <div className="bg-gray-900 dark:bg-black font-mono text-[11px] select-all max-h-44 overflow-y-auto">
            {[
              { time: '0.2s', msg: '✅ Starting extraction...', color: 'text-green-400' },
              { time: '1.4s', msg: '✅ Document uploaded to Azure CU', color: 'text-green-400' },
              { time: '2.1s', msg: '✅ Email body analysed via OpenAI', color: 'text-green-400' },
              { time: '3.5s', msg: '⏳ Polling for extraction results...', color: 'text-blue-400' },
              { time: '4.8s', msg: '✅ 12 fields extracted successfully', color: 'text-green-400' },
              { time: '5.1s', msg: '✅ Completed in 5.1s', color: 'text-green-400' },
            ].map((log, i) => (
              <div key={i} className="px-3 py-1 border-b border-gray-800 flex gap-2 hover:bg-gray-800">
                <span className="text-gray-500 whitespace-nowrap min-w-[40px]">{log.time}</span>
                <span className={log.color}>{log.msg}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Activity Log mockup */}
        <div className="mt-4 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 overflow-hidden max-w-lg">
          {/* Activity header */}
          <div className="px-4 py-2.5 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between">
            <span className="text-xs font-semibold text-gray-900 dark:text-gray-100">Activity Log</span>
            <button className="text-xs text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1">
              <RefreshCw className="w-3 h-3" /> Refresh
            </button>
          </div>

          {/* Activity entries */}
          <div className="p-4 space-y-3">
            {[
              { type: 'user', dot: 'bg-blue-500', text: <><strong>John Doe</strong> edited Issuer: "ABC Co" → "ABC Corporation"</>, time: '2 hours ago' },
              { type: 'user', dot: 'bg-blue-500', text: <><strong>John Doe</strong> published request</>, time: '3 hours ago' },
              { type: 'bot', dot: 'bg-purple-500', text: <><strong>AI</strong> extracted 12 fields from Offering Circular.pdf</>, time: '3 hours ago' },
              { type: 'system', dot: 'bg-gray-400', text: <>Request created via Outlook Plugin</>, time: '3 hours ago' },
            ].map((entry, i) => (
              <div key={i} className="flex items-start gap-2 group">
                <div className={`w-2 h-2 rounded-full ${entry.dot} mt-1.5 flex-shrink-0`} />
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-gray-700 dark:text-gray-300">{entry.text}</p>
                  <p className="text-[10px] text-gray-400 dark:text-gray-500">{entry.time}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-3 space-y-2 text-sm text-gray-600 dark:text-gray-400">
          <p><strong>Processing Log:</strong> Terminal-style (dark background, mono font) showing timestamped extraction steps. Color-coded: green ✅ = success, red ❌ = error, blue ⏳ = polling, yellow ⚠️ = warning.</p>
          <p><strong>Activity Log:</strong> Timeline of all actions on the request. Blue dot = user action, purple dot = AI/bot action, gray dot = system event. Each entry shows who, what, and when.</p>
          <p><strong>Refresh:</strong> Blue "Refresh" button in the Activity Log header fetches the latest entries.</p>
        </div>
      </Section>

      {/* Tips */}
      <section className="space-y-4">
        <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">Tips</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="rounded-xl bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 p-4">
            <div className="flex items-center gap-2 mb-1.5">
              <Info className="h-4 w-4 text-blue-600 dark:text-blue-400" />
              <span className="text-xs font-semibold text-blue-900 dark:text-blue-200">Use Status Filters</span>
            </div>
            <p className="text-[11px] text-blue-700 dark:text-blue-300 leading-relaxed">Filter by "Reviewing" or "Extracted" to quickly find requests that need your attention.</p>
          </div>
          <div className="rounded-xl bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 p-4">
            <div className="flex items-center gap-2 mb-1.5">
              <Zap className="h-4 w-4 text-purple-600 dark:text-purple-400" />
              <span className="text-xs font-semibold text-purple-900 dark:text-purple-200">Auto-Analyze Saves Time</span>
            </div>
            <p className="text-[11px] text-purple-700 dark:text-purple-300 leading-relaxed">Keep "Auto-analyze" enabled when creating requests so AI extraction begins immediately.</p>
          </div>
          <div className="rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 p-4">
            <div className="flex items-center gap-2 mb-1.5">
              <AlertCircle className="h-4 w-4 text-amber-600 dark:text-amber-400" />
              <span className="text-xs font-semibold text-amber-900 dark:text-amber-200">Review Low Confidence Fields</span>
            </div>
            <p className="text-[11px] text-amber-700 dark:text-amber-300 leading-relaxed">Focus on fields with red/yellow confidence bars - these are most likely to need correction.</p>
          </div>
          <div className="rounded-xl bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 p-4">
            <div className="flex items-center gap-2 mb-1.5">
              <Layers className="h-4 w-4 text-green-600 dark:text-green-400" />
              <span className="text-xs font-semibold text-green-900 dark:text-green-200">Duplicate Detection</span>
            </div>
            <p className="text-[11px] text-green-700 dark:text-green-300 leading-relaxed">The system detects duplicate documents automatically when uploading to prevent processing the same file twice.</p>
          </div>
        </div>
      </section>

      {/* Related Modules */}
      <section>
        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">Related Modules</h3>
        <div className="flex flex-wrap gap-2">
          {[
            { id: 'templates', label: 'Templates', icon: Layers },
            { id: 'email-queue', label: 'Email Queue', icon: Mail },
            { id: 'documents', label: 'Documents', icon: FileText },
          ].map((mod) => {
            const Icon = mod.icon
            return (
              <button key={mod.label} onClick={() => onNavigate(mod.id)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-xs font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors cursor-pointer">
                <Icon className="h-3.5 w-3.5" />
                {mod.label}
                <ArrowRight className="h-3 w-3 text-gray-400" />
              </button>
            )
          })}
        </div>
      </section>
    </div>
  )
}
